import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { debounce } from "lodash";
import {
  MagnifyingGlassIcon,
  XMarkIcon,
  CheckIcon,
  ChevronDownIcon,
  ClockIcon,
  StarIcon,
} from "@heroicons/react/24/outline";
import axios from "../../../api/axios";
import { ResourceLoading } from "../ResourceLoading";
import { PMDialog } from "../PMDialog";
import { useAutofocusOnOpen } from "../useAutofocusOnOpen";

const FETCH_URL = "/projects/";

const mapProjectToItem = (row) => ({
  id: row.id,
  value: row.id,
  primary: row.displayTitle ?? row.name ?? "",
  secondary: row?.customer?.displayTitle ?? null,
  object: row,
});

const dedupeById = (rows) => {
  const seen = new Set();
  return rows.filter((row) => {
    if (!row?.id || seen.has(row.id)) return false;
    seen.add(row.id);
    return true;
  });
};

const itemMatchesQuery = (item, query) => {
  const needle = query.trim().toLowerCase();
  if (!needle) return true;

  return [
    item.primary,
    item.secondary,
    item.object?.name,
    item.object?.project_number,
    item.object?.parent_project?.displayTitle,
    item.object?.parent_project?.name,
    item.object?.parent_project?.project_number,
  ]
    .filter(Boolean)
    .some((value) => String(value).toLowerCase().includes(needle));
};

export const ProjectSelector = (props) => {
  const {
    setFieldValue,
    errors,
    onChange,
    fields,
    showField,
    activeOnly,
  } = props;

  const name = props?.name ?? "project_id";
  const label = props?.label ?? "Project";
  /** i18n key for the “no project” row and empty trigger (default: literal “Ingen valgt”). */
  const noneLabelKey = props.noneLabelKey;

  const { t } = useTranslation();
  const nonePrimary = noneLabelKey ? t(noneLabelKey) : "Ingen valgt";
  const providedFavoriteProjects = useMemo(
    () => props.favoriteProjects ?? props.favorites ?? [],
    [props.favoriteProjects, props.favorites]
  );

  const excludeProjectIdSet = useMemo(() => {
    const raw = props.excludeProjectIds ?? props.excludeIds;
    if (!raw || !raw.length) return null;
    const set = new Set();
    raw.forEach((id) => {
      const n = Number(id);
      if (!Number.isNaN(n) && n > 0) set.add(n);
    });
    return set.size ? set : null;
  }, [props.excludeProjectIds, props.excludeIds]);

  // ── Dialog state ──────────────────────────────────────────────────────────
  const [open, setOpen] = useState(false);

  // ── Search & list state ───────────────────────────────────────────────────
  const [query, setQuery] = useState("");
  const [items, setItems] = useState([]);
  const [suggestedItems, setSuggestedItems] = useState([]);
  const [favoriteItems, setFavoriteItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const searchInputRef = useAutofocusOnOpen(open);

  // ── Selected state ────────────────────────────────────────────────────────
  const [selected, setSelected] = useState({
    id: null,
    field1: nonePrimary,
    field2: null,
  });

  useEffect(() => {
    if (props?.selected?.id) {
      setSelected({
        id: props.selected.id,
        field1: props.selected.displayTitle ?? nonePrimary,
        field2: props.selected?.customer?.displayTitle ?? null,
        syncStatus: props.selected?.syncStatus,
      });
    } else {
      setSelected({
        id: null,
        field1: nonePrimary,
        field2: null,
      });
    }
  }, [props.selected, nonePrimary]);

  // ── Open/close ────────────────────────────────────────────────────────────
  const handleOpen = () => {
    if (props.disabled) return;
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setQuery("");
  };

  // ── Propagate selection ───────────────────────────────────────────────────
  const handleSelect = (item) => {
    setSelected({
      id: item.id,
      field1: item.primary ?? nonePrimary,
      field2: item.secondary ?? null,
    });

    if (setFieldValue) setFieldValue(name, item.id != null ? item.id : null);
    if (props.onParentObjectChange) {
      if (item.id == null) {
        props.onParentObjectChange(null);
      } else {
        const obj = item.object;
        props.onParentObjectChange(
          obj && typeof obj === "object"
            ? obj
            : { id: item.id, displayTitle: item.primary, name: item.primary }
        );
      }
    }
    if (onChange) {
      onChange({
        target: {
          name,
          value: item.object?.id ?? item.id,
          object: item.object,
          data: item,
        },
      });
    }
    handleClose();
  };

  // ── Data fetching ─────────────────────────────────────────────────────────
  const fetchItems = useCallback(
    async (searchQuery) => {
      setLoading(true);
      try {
        const response = await axios({
          method: "get",
          url: FETCH_URL,
          params: {
            query: searchQuery || undefined,
            page: 1,
            sortBy: "created_at",
            sort: "desc",
            active: activeOnly || undefined,
            fields: fields || "*",
            include_suggested: true,
            offer_scope: "all",
          },
        });

        const projectRows = Array.isArray(response.data?.data)
          ? response.data.data
          : [];
        const suggestedRows = Array.isArray(response.data?.suggestedProjects)
          ? response.data.suggestedProjects
          : [];
        const responseFavoriteRows =
          response.data?.favoriteProjects ??
          response.data?.favorite_projects ??
          response.data?.favorites ??
          [];
        const favoriteRows = [
          ...(Array.isArray(providedFavoriteProjects)
            ? providedFavoriteProjects
            : []),
          ...(Array.isArray(responseFavoriteRows) ? responseFavoriteRows : []),
        ];
        const maybeActiveRows = (rows) =>
          activeOnly ? rows.filter((row) => row?.active !== false) : rows;

        setFavoriteItems(
          dedupeById(maybeActiveRows(favoriteRows)).map(mapProjectToItem)
        );
        setSuggestedItems(
          dedupeById(maybeActiveRows(suggestedRows)).map(mapProjectToItem)
        );
        setItems(dedupeById(maybeActiveRows(projectRows)).map(mapProjectToItem));
      } catch {
        setItems([]);
        setSuggestedItems([]);
        setFavoriteItems([]);
      } finally {
        setLoading(false);
      }
    },
    [activeOnly, fields, providedFavoriteProjects]
  );

  const debouncedFetch = useCallback(
    debounce((q) => fetchItems(q), 300),
    [fetchItems]
  );

  useEffect(() => {
    if (!open) return;
    debouncedFetch(query);
    return () => debouncedFetch.cancel();
  }, [query, open, debouncedFetch]);

  // ── Trigger button ────────────────────────────────────────────────────────
  const hasSelection = selected?.id != null;

  const visibleSections = useMemo(() => {
    const notExcluded = (row) =>
      !row?.id || !excludeProjectIdSet || !excludeProjectIdSet.has(row.id);

    const favorites = favoriteItems.filter(
      (item) => itemMatchesQuery(item, query) && notExcluded(item)
    );
    const favoriteIds = new Set(favorites.map((item) => item.id));
    const suggested = suggestedItems
      .filter((item) => itemMatchesQuery(item, query) && notExcluded(item))
      .filter((item) => !favoriteIds.has(item.id));
    const promotedIds = new Set([
      ...favorites.map((item) => item.id),
      ...suggested.map((item) => item.id),
    ]);
    const projects = items
      .filter((item) => itemMatchesQuery(item, query) && notExcluded(item))
      .filter((item) => !promotedIds.has(item.id));

    return [
      {
        key: "favorites",
        title: t("project_selector.favorites"),
        icon: StarIcon,
        rows: favorites,
      },
      {
        key: "suggested",
        title: t("project_selector.suggested"),
        icon: ClockIcon,
        rows: suggested,
      },
      {
        key: "projects",
        title: query.trim()
          ? t("project_selector.search_results")
          : t("project_selector.projects"),
        icon: null,
        rows: projects,
      },
    ].filter((section) => section.rows.length > 0);
  }, [excludeProjectIdSet, favoriteItems, items, query, suggestedItems, t]);

  const allVisibleItems = useMemo(
    () => visibleSections.flatMap((section) => section.rows),
    [visibleSections]
  );

  const renderItem = (item) => {
    const isSelected = item.id === selected?.id;
    const obj = item.object;
    const isSub = !!(obj?.parent_project_id || obj?.parent_project);
    const isOffer = obj?.is_offer === true;
    return (
      <li
        key={item.id ?? "none"}
        onClick={() => handleSelect(item)}
        className={[
          "flex items-center justify-between px-4 py-3 cursor-pointer transition-colors",
          isSelected
            ? "bg-blue-50 text-blue-700"
            : "hover:bg-gray-50 text-gray-900",
        ].join(" ")}
      >
        <div className={`min-w-0 ${isSub ? "pl-3 border-l-2 border-slate-200" : ""}`}>
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-sm font-medium truncate">{item.primary}</p>
            {isOffer ? (
              <span className="inline-flex shrink-0 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-900">
                {t("project_selector.offer")}
              </span>
            ) : null}
          </div>
          {item.secondary && (
            <p className="text-xs text-gray-500 truncate mt-0.5">
              {item.secondary}
            </p>
          )}
          {obj?.parent_project ? (
            <p className="text-xs text-slate-600 truncate mt-0.5">
              {t("project_selector.sub_of")}:{" "}
              {obj.parent_project.displayTitle ||
                obj.parent_project.name ||
                obj.parent_project.project_number}
            </p>
          ) : null}
        </div>
        {isSelected && (
          <CheckIcon className="h-4 w-4 flex-shrink-0 ml-3 text-blue-600" />
        )}
      </li>
    );
  };

  const triggerButton =
    showField !== false ? (
      <div>
        {label !== false && (
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t(label)}
          </label>
        )}
        <button
          type="button"
          disabled={props.disabled}
          onClick={handleOpen}
          className={[
            "relative w-full flex items-center justify-between",
            "rounded-md border bg-white px-3 py-2 text-sm shadow-sm",
            "transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500",
            props.disabled
              ? "cursor-not-allowed border-gray-200 text-gray-400"
              : hasSelection
              ? "border-blue-400 text-gray-900 hover:border-blue-500"
              : "border-gray-300 text-gray-500 hover:border-gray-400",
          ].join(" ")}
        >
          <div className="min-w-0">
            <span className="truncate">{selected.field1}</span>
            {selected.field2 && (
              <span className="ml-2 text-xs text-gray-400 truncate">
                {selected.field2}
              </span>
            )}
          </div>
          <ChevronDownIcon className="h-4 w-4 flex-shrink-0 text-gray-400 ml-2" />
        </button>
        {errors?.[name] && (
          <p className="mt-1 text-xs text-red-600">{errors[name]}</p>
        )}
      </div>
    ) : null;

  // ── Dialog content ────────────────────────────────────────────────────────
  const dialogContent = (
    <div className="flex flex-col h-full" style={{ minHeight: 0 }}>
      {/* Header */}
      <div className="flex-shrink-0 p-3 sm:p-4 border-b border-gray-200">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-base font-semibold text-gray-900">{t(label)}</h3>
          <button
            type="button"
            onClick={handleClose}
            className="rounded-md p-1.5 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="relative">
          <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
            <MagnifyingGlassIcon className="h-4 w-4 text-gray-400" />
          </div>
          <input
            ref={searchInputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Søk etter prosjekt..."
            autoFocus
            className="block w-full rounded-md border border-gray-300 bg-white py-2 pl-9 pr-9 text-sm placeholder-gray-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
          {query && (
            <button
              type="button"
              onClick={() => setQuery("")}
              className="absolute inset-y-0 right-0 flex items-center pr-3"
            >
              <XMarkIcon className="h-4 w-4 text-gray-400 hover:text-gray-600" />
            </button>
          )}
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto" style={{ minHeight: 0 }}>
        {loading ? (
          <div className="flex items-center justify-center py-10">
            <ResourceLoading />
          </div>
        ) : (
          <div>
            <ul className="divide-y divide-gray-100">
              {renderItem({
                id: null,
                value: null,
                primary: nonePrimary,
                secondary: null,
              })}
            </ul>
            {allVisibleItems.length === 0 ? (
              <div className="px-4 py-8 text-center text-sm text-gray-500">
                {query.trim()
                  ? t("project_selector.no_projects_found")
                  : t("project_selector.no_projects_to_show")}
              </div>
            ) : (
              visibleSections.map((section) => {
                const SectionIcon = section.icon;
                return (
                  <div key={section.key}>
                    <div className="flex items-center gap-2 border-y border-gray-100 bg-gray-50 px-4 py-2 text-xs font-semibold uppercase text-gray-600">
                      {SectionIcon && <SectionIcon className="h-4 w-4" />}
                      <span>{section.title}</span>
                    </div>
                    <ul className="divide-y divide-gray-100">
                      {section.rows.map((item) => renderItem(item))}
                    </ul>
                  </div>
                );
              })
            )}
          </div>
        )}
      </div>
    </div>
  );

  return (
    <>
      {triggerButton}
      <PMDialog
        open={open}
        onClose={handleClose}
        size="md"
        dense
        closeOnOutsideClick
        zIndex={props.zIndex}
      >
        {dialogContent}
      </PMDialog>
    </>
  );
};
